import * as Sentry from "@sentry/react";
import { API_ERROR_NAMES } from "@coffitivity/shared";
import type { IApiClient } from "@/lib/__shared__/apiClient";

const API_BASE = import.meta.env.VITE_USER_SERVER_URL;

export interface StartSessionResult {
	sessionId: string;
	secondsUsedToday: number;
	limitSeconds: number | null;
	bonusSecondsRemaining: number | null;
}

export interface EndSessionResult {
	durationSeconds: number | null;
	secondsUsedToday: number;
	bonusSecondsRemaining: number | null;
}

export interface DailyUsageResult {
	secondsUsedToday: number;
	limitSeconds: number | null;
	bonusSecondsRemaining: number | null;
}

export class DailyLimitExceededError extends Error {
	constructor() {
		super("Daily listening limit exceeded.");
		this.name = "DailyLimitExceededError";
	}
}

export interface IListeningTrackingService {
	startSession(_trackId: string): Promise<StartSessionResult>;
	endSession(_sessionId: string): Promise<EndSessionResult | null>;
	getDailyUsage(): Promise<DailyUsageResult>;
	/** Fire-and-forget session end for onbeforeunload. Uses raw fetch with keepalive — cannot use ApiClient (async not allowed). */
	endSessionOnUnload(_sessionId: string): void;
}

/**
 * ListeningTrackingService wraps the listening session API endpoints.
 *
 * Key features:
 * - Client-generated session IDs (crypto.randomUUID) so the ID is available immediately
 * - Request serializer (promise chain) to prevent race conditions from rapid play/pause
 * - sendBeacon support for reliable session-end on page unload
 */
export class ListeningTrackingService implements IListeningTrackingService {
	private requestChain: Promise<unknown> = Promise.resolve();
	private apiClient: IApiClient;

	constructor(apiClient: IApiClient) {
		this.apiClient = apiClient;
	}

	/**
	 * Enqueue an operation onto the serial promise chain.
	 * Each start/end call waits for the previous one to complete.
	 * Errors are caught per-request so the chain continues.
	 */
	private enqueue<T>(fn: () => Promise<T>): Promise<T> {
		const result = this.requestChain.then(fn, fn);
		// Keep the chain going regardless of success/failure
		this.requestChain = result.catch((error) => {
			// Log the error but don't break the chain - best effort for each request
			Sentry.captureException(error);
		});
		return result;
	}

	async startSession(_trackId: string): Promise<StartSessionResult> {
		return this.enqueue(async () => {
			const sessionId = crypto.randomUUID();

			const response = await this.apiClient.fetch(
				`${API_BASE}/listening/start`,
				{
					method: "POST",
					body: JSON.stringify({ sessionId, trackId: _trackId }),
				},
			);

			// Read the error body exactly once — a Response body cannot be
			// consumed twice, so the daily-limit check and the generic error
			// path have to share the same parsed payload.
			if (!response.ok) {
				const json = await response.json().catch(() => ({}));

				if (
					response.status === 403 &&
					json.code === API_ERROR_NAMES.DAILY_LIMIT_EXCEEDED
				) {
					throw new DailyLimitExceededError();
				}

				throw new Error(
					json.message ?? "Network error: failed to start listening session.",
				);
			}

			const json = await response.json();
			return json.data as StartSessionResult;
		});
	}

	/**
	 * Fire-and-forget session end for use in onbeforeunload.
	 * Uses raw globalThis.fetch with keepalive — ApiClient cannot be used here
	 * because async operations are not reliable during page unload.
	 * This is the only call site that still requires an explicit access token.
	 */
	endSessionOnUnload(_sessionId: string): void {
		const token = this.apiClient.getCachedToken();
		if (!token) return;

		globalThis
			.fetch(`${API_BASE}/listening/${_sessionId}/end`, {
				method: "PATCH",
				headers: {
					"Content-type": "application/json; charset=UTF-8",
					Authorization: `Bearer ${token}`,
				},
				keepalive: true,
			})
			.catch(() => {
				// Best effort — if this fails, orphan cleanup on next start handles it
			});
	}

	async endSession(_sessionId: string): Promise<EndSessionResult | null> {
		return this.enqueue(async () => {
			const response = await this.apiClient.fetch(
				`${API_BASE}/listening/${_sessionId}/end`,
				{
					method: "PATCH",
				},
			);

			if (!response.ok) {
				const json = await response.json().catch(() => ({}));
				throw new Error(json.message ?? "Failed to end listening session.");
			}

			const json = await response.json();
			return json.data as EndSessionResult;
		});
	}

	async getDailyUsage(): Promise<DailyUsageResult> {
		try {
			const response = await this.apiClient.fetch(
				`${API_BASE}/listening/daily-usage`,
				{
					method: "GET",
				},
			);

			if (!response.ok) {
				const json = await response.json().catch(() => ({}));
				throw new Error(json.message ?? "Failed to fetch daily usage.");
			}

			const json = await response.json();
			return json.data as DailyUsageResult;
		} catch (error) {
			Sentry.captureException(error);
			// Return safe defaults on error so the app doesn't break
			return {
				secondsUsedToday: 0,
				limitSeconds: 3600,
				bonusSecondsRemaining: null,
			};
		}
	}
}
